import { parse } from 'query-string';
import { getTenancySelector, getEnvironmentSelector } from '@americanexpress/travel-selectors';
import {
  e1, e2, e3, CONSUMER_TRAVEL, STAGING, TRAVEL_ISEATZ, FAUX_LEVEL_URL_PARAM,
  PREBOOKING,
} from './devFlagConstants';

const validEnvs = [e1, e2, e3, undefined];
const validTenancies = [
  CONSUMER_TRAVEL,
  STAGING,
  TRAVEL_ISEATZ,
  PREBOOKING,
  undefined,
];

const checkIfEnvsTenancyIsValid = ({
  env,
  tenancy,
}) => validEnvs.includes(env) && validTenancies.includes(tenancy);

export const isDevFlagEnabledInEnv = (env, envToStopAt) => envToStopAt >= env;

export const getCurrentConsumerTravelEnv = (configEnv, configTenancy) => {
  const configEnvTenancy = { env: configEnv, tenancy: configTenancy };
  // This is the accepted check to see if you are in Server Side or client side
  if (!global.BROWSER) {
    return configEnvTenancy;
  }

  // This is a collection of queries being added along with the url
  // and we use the queries to check if _feature_switch_faux_level query is appended.
  // The faux query should have the format: env_tenancy
  const query = parse(global.location.search);

  const fauxEnvTenancyQuery = query[FAUX_LEVEL_URL_PARAM];

  if (!fauxEnvTenancyQuery) {
    return configEnvTenancy;
  }

  const [fauxEnv, fauxTenancy] = fauxEnvTenancyQuery.split('_');
  const fauxEnvTenancy = { env: fauxEnv, tenancy: fauxTenancy };

  if (!checkIfEnvsTenancyIsValid(fauxEnvTenancy)) {
    return configEnvTenancy;
  }

  if (!configEnv || !configTenancy) {
    return fauxEnvTenancy;
  }

  if (configEnv > fauxEnv) {
    return configEnvTenancy;
  }

  return fauxEnvTenancy;
};

const validateConfigForValue = (configElements, value) => configElements.length === 0
  || configElements.includes(value);

/**
 * Returns a boolean value to identify if a feature should be enabled
 * given the current environment. When called with an environment label,
 * all environments up to and including will have the feature activated.
 * In E0, by default, all features are activated until disabled.
 *
 * @param envToStopAt  [string|bool]  "e3" will result in feature activation within e0, e1, e2, e3.
 *                                    "e2" will result in feature activation within e0, e1, e2.
 *                                    "e1" will result in feature activation within e0, e1.
 *                                    any other string will result in feature activation
 *                                    exclusively in E0.
 *                                    'false' will result in deactivated features at all levels.
 * @param tenancies [string]
 *                [CONSUMER-TRAVEL | STAGING | PREBOOKING]
 *                Which of our three tenancies this feature should be present in
 * @param state        object  The redux store as provided by One App.
 * @returns {boolean}
 */

export const devFlagStopAt = (state, { envToStopAt, tenancies = [] }) => {
  const configEnv = getEnvironmentSelector(state);
  const configTenancy = getTenancySelector(state);
  const envTenancy = getCurrentConsumerTravelEnv(configEnv, configTenancy);
  const { env, tenancy } = envTenancy;

  if (envToStopAt === false || !checkIfEnvsTenancyIsValid(envTenancy)) {
    return false;
  }

  if (env === undefined && tenancy === undefined) {
    return true; // All features are activated in E0 by default, unless explicitly deactivated.
  }

  if (!validateConfigForValue(tenancies, tenancy)) return false;

  return isDevFlagEnabledInEnv(env, envToStopAt);
};

export const devFlagByConfigs = (state, configs) => configs.some(
  (config) => devFlagStopAt(state, config)
);
